/**
 * Tagesuebersicht (F-35 C) - auf einen Blick: Energie und Makros gegen das
 * Ziel des Tages (Trainings- oder Ruhetag), Verteilung in Prozent, weitere
 * Naehrstoffe und Portionen je Lebensmittelgruppe. Abweichungen neutral -
 * ein einzelner Tag ist kein Versagen (Roadmap F-35, Vorschlag).
 */
import { Link } from 'react-router-dom';

import { tagVollstaendig } from '@/db/repositories/ernaehrung';
import { GEZAEHLTE_GRUPPEN, makroVerteilung, ZIEL_ART, type TagZeile } from '@/domain/ernaehrung/ueberblick';
import { GRUPPEN, MAKROS, MIKROS, type LebensmittelGruppe, type MakroKey, type Mikro, type Tagesziele } from '@/domain/ernaehrung/typen';
import type { Summe, WertKey } from '@/domain/ernaehrung/naehrwerte';
import { Karte } from '@/modules/training/auswertung/Karte';
import { kcal, mitEinheit, zahl } from '@/modules/ernaehrung/format';

const ZEICHEN = { spanne: '≈', mindestens: '≥', hoechstens: '≤' } as const;

/** Balken gegen das Ziel: das Ziel (Strich) liegt bei 80 % der Breite, darueber geht es weiter. Ohne Ziel nur die Zahl. */
export function ZielBalken({ label, k, wert, ziel, einheit, eingerueckt, unvollstaendig }: {
  label: string; k?: keyof Tagesziele; wert: number | undefined; ziel?: number; einheit: string; eingerueckt?: boolean; unvollstaendig?: boolean;
}) {
  const breite = ziel ? Math.min(100, ((wert ?? 0) / (ziel / 0.8)) * 100) : 0;
  const text = wert === undefined ? '–' : einheit === 'kcal' ? kcal(wert) : `${zahl(wert)} ${einheit}`;
  return (
    <div className={eingerueckt ? 'pl-3' : ''}>
      <div className="flex items-baseline gap-2 text-[12px]">
        <span className={eingerueckt ? 'flex-1 text-ink-muted' : 'flex-1'}>{label}{unvollstaendig ? <span className="text-ink-faint" title="Bei mindestens einem Eintrag fehlt dieser Wert."> · unvollständig</span> : null}</span>
        <span className="tabular-nums">{text}</span>
        <span className="w-20 text-right tabular-nums text-ink-faint">{ziel !== undefined && k ? `${ZEICHEN[ZIEL_ART[k]]} ${zahl(ziel, 0)}` : 'kein Ziel'}</span>
      </div>
      {ziel !== undefined && (
        <div className="relative mt-0.5 h-1.5 rounded-full bg-surface-raised" role="img" aria-label={`${label}: ${text} von ${zahl(ziel, 0)}`}>
          <div className="h-full rounded-full bg-ink-muted/70" style={{ width: `${breite}%` }} />
          <div className="absolute -top-0.5 h-2.5 w-0.5 bg-ink" style={{ left: '80%' }} title="Ziel" />
        </div>
      )}
    </div>
  );
}

function Verteilung({ summe }: { summe: Summe }) {
  const v = makroVerteilung(summe.werte);
  if (!v) return null;
  const teile: [keyof typeof v, string][] = [['eiweiss', 'Eiweiß'], ['kh', 'Kohlenhydrate'], ['fett', 'Fett']];
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[12px] text-ink-muted">Verteilung der Energie aus den Makros</span>
      <div className="flex h-3 overflow-hidden rounded-full" role="img" aria-label={teile.map(([k, l]) => `${l} ${v[k]} %`).join(', ')}>
        {teile.map(([k]) => <div key={k} style={{ width: `${v[k]}%`, background: `var(--color-makro-${k})` }} />)}
      </div>
      <div className="flex flex-wrap gap-x-3 text-[11px] text-ink-muted">
        {teile.map(([k, l]) => <span key={k} className="flex items-center gap-1"><span className="size-2 rounded-full" style={{ background: `var(--color-makro-${k})` }} />{l} {v[k]} %</span>)}
      </div>
    </div>
  );
}

export function Tagesuebersicht({ zeile, gruppen, mikros, mitTraining }: {
  zeile: TagZeile;
  gruppen: Partial<Record<LebensmittelGruppe, number>>;
  mikros: Mikro[];
  /** Gibt es ueberhaupt eigene Ziele fuer Trainingstage? */
  mitTraining: boolean;
}) {
  const w = zeile.summe.werte;
  const z = zeile.ziele;
  const offen = (k: WertKey) => zeile.summe.unvollstaendig.has(k) && zeile.summe.posten > 0 && w[k as MakroKey] !== undefined;
  const balken = (k: MakroKey & keyof Tagesziele, eingerueckt = false) => (
    <ZielBalken key={k} k={k} label={MAKROS[k].label} wert={w[k]} ziel={z[k]} einheit={MAKROS[k].einheit} eingerueckt={eingerueckt} unvollstaendig={offen(k)} />
  );
  const ohneZiele = !Object.keys(z).length;
  return (
    <Karte titel="Tagesübersicht" frage={`${zeile.training ? 'Trainingstag' : 'Ruhetag'}${mitTraining ? ` – Ziele für ${zeile.training ? 'Trainingstage' : 'Ruhetage'}` : ''}. Wo stehe ich heute?`}>
      <ZielBalken k="kcal" label="Energie" wert={w.kcal} ziel={z.kcal} einheit="kcal" />
      <div className="flex flex-col gap-1.5">
        {balken('eiweiss')}
        {balken('kh')}{balken('zucker', true)}
        <ZielBalken label="Fett" k="fett" wert={w.fett} ziel={z.fett} einheit="g" unvollstaendig={offen('fett')} />
        <ZielBalken label="davon gesättigte Fettsäuren" wert={w.gesFett} einheit="g" eingerueckt unvollstaendig={offen('gesFett')} />
        {balken('ballast')}
        {balken('salz')}
      </div>
      <Verteilung summe={zeile.summe} />
      {mikros.length > 0 && (
        <div className="flex flex-col gap-0.5">
          <span className="text-[12px] text-ink-muted">Weitere Nährstoffe</span>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[12px] sm:grid-cols-3">
            {mikros.map((m) => (
              <div key={m} className="flex justify-between gap-2">
                <dt className="text-ink-muted">{MIKROS[m].label}</dt>
                <dd className="tabular-nums">{w.mikro?.[m] !== undefined ? mitEinheit(m, w.mikro[m]) : '–'}{zeile.summe.posten > 0 && zeile.summe.unvollstaendig.has(m) ? <span className="text-ink-faint" title="Nicht alle Lebensmittel haben diesen Wert."> *</span> : null}</dd>
              </div>
            ))}
          </dl>
          <span className="text-[11px] text-ink-faint">* unvollständig: Nicht alle Lebensmittel haben diesen Wert – kein Mangel, sondern fehlende Daten.</span>
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <span className="text-[12px] text-ink-muted">Portionen je Lebensmittelgruppe</span>
        <div className="flex flex-wrap gap-1.5 text-[12px]">
          {GEZAEHLTE_GRUPPEN.map((g) => <span key={g} className="rounded-md bg-surface-raised px-2 py-0.5">{GRUPPEN[g].label} <span className="font-semibold tabular-nums">{zahl(gruppen[g] ?? 0)}</span></span>)}
        </div>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-ink-muted">
        <input type="checkbox" checked={zeile.vollstaendig} onChange={(e) => void tagVollstaendig(zeile.datum, e.target.checked)} />
        Tag vollständig erfasst ({zeile.mahlzeiten} {zeile.mahlzeiten === 1 ? 'Mahlzeit' : 'Mahlzeiten'}) – nur vollständige Tage zählen im Verlauf
      </label>
      {ohneZiele && <p className="text-[12px] text-ink-faint">Noch keine Ziele. <Link to="/ernaehrung/verlauf#ziele" className="text-accent-text hover:underline">Ziele festlegen</Link></p>}
    </Karte>
  );
}
